// Note the api does not support active saving for smoking usages
import { useInsuredStore } from "#src/stores/insured.ts";
import { mdiDelete, mdiPlus } from "@mdi/js";
import { type Pinia, storeToRefs } from "pinia";
import { computed, type Ref, ref } from "vue";
import {
  SMOKING_STATUS,
  type SmokingStatus,
} from "#src/constants/smoking-usage.ts";

import {
  useLastUseDateProperty,
  useCategoryProperty,
  useFrequencyProperty,
} from "#src/composables/smoking-usage-form-property.composable.ts";

import {
  useInsuredSmokerButtonsProperty,
  useInsuredSmokerSelectProperty,
} from "#src/composables/insured-form-property.composable.js";

import {
  FormBasicSubheader,
  FormButton,
  FormExpansionPanels,
  FormExpansionPanel,
  FormExpansionPanelBody,
  FormExpansionPanelTitle,
  validateFormStructure,
  type FormField,
  type FormBuilderRow,
} from "#src/components/form-builder/FormBuilderTemplates.ts";
import { isServerSide } from "#src/util/helpers.ts";
import { type InsuredType } from "#src/types/Insured.ts";

export interface SmokerFormArgs {
  skipCb?: () => void;
  dense?: boolean;
  titleId?: string;
}

export interface SmokerForm {
  buildStructure(): FormBuilderRow[];
  validate(args: Partial<{ throwOnFailure: boolean }>): boolean;
  activeUsageIndex: Ref<null | number>;
  reactiveDependencies: Ref[];
}

export function useSmokerForm(
  pinia: Pinia,
  module: InsuredType,
  { skipCb, dense, titleId }: SmokerFormArgs = {}
): SmokerForm {
  const insuredStore = useInsuredStore(module, pinia);
  const { smoker, smoking_statuses } = storeToRefs(insuredStore);

  const activeUsageIndex = ref<null | number>(null);

  const usesSmokingUsage: SmokingStatus[] = [
    SMOKING_STATUS.PREVIOUSLY,
    SMOKING_STATUS.CURRENTLY,
  ];

  const deletable = computed(() => smoking_statuses.value.length > 1);
  const smokingStatusCount = computed(() => smoking_statuses.value.length);
  const smokingType = computed(() => smoker.value.model);
  const showSmokingUsage = computed(() => {
    if (!smoker.value.model) return false;

    return usesSmokingUsage.includes(smoker.value.model);
  });

  function addUsage() {
    const id = insuredStore.createSmokingUsage();
    const index = smoking_statuses.value.findIndex((v: string) => v === id);
    if (index > -1) activeUsageIndex.value = index;
  }

  function buildSmokingStatusField() {
    let property = useInsuredSmokerButtonsProperty;
    if (dense) property = useInsuredSmokerSelectProperty;

    return property(pinia, module, {
      mdColSize: 12,
      justify: "center",
      ariaLabelledBy: titleId,
      onAfterSave() {
        if (smoker.value.model === SMOKING_STATUS.NEVER) {
          smoking_statuses.value.forEach((id: string) =>
            insuredStore.deleteSmokingUsage(id)
          );
          if (skipCb) skipCb();
        } else if (
          smoker.value.model &&
          usesSmokingUsage.includes(smoker.value.model)
        ) {
          if (!smoking_statuses.value.length) addUsage();
        }
      },
    });
  }

  function buildAddUsageButton() {
    return FormButton("Add Usage", "add-usage", {
      prependIcon: mdiPlus,
      onClick: addUsage,
      variant: "outlined",
      rounded: true,
      color: "primary",
      class: "text-none text-left mb-5",
    });
  }

  function buildUsageTitle(usageId: string, index: number) {
    return FormBasicSubheader(
      `Smoking Status ${index + 1}`,
      usageId + "subheader",
      {
        class: "text-white mb-0",
      }
    );
  }

  function buildDeleteUsageButton(usageId: string) {
    return FormButton("", "delete-usage", {
      icon: mdiDelete,
      variant: "text",
      onClick: () => insuredStore.deleteSmokingUsage(usageId),
      color: "error",
      class: "text-none",
      style: "position: absolute; right: -42px",
      size: "small",
    });
  }

  function buildCategoryField(usageId: string, index: number) {
    const key = `smoking_status.${index}.category`;
    return useCategoryProperty(pinia, usageId, { save: () => {}, key });
  }

  function buildFrequencyField(usageId: string, index: number) {
    const key = `smoking_status.${index}.frequency`;
    return useFrequencyProperty(pinia, usageId, {
      save: () => {},
      key,
    });
  }

  function buildLastUseDateField(usageId: string, index: number) {
    const key = `smoking_status.${index}.last_use_date`;
    return useLastUseDateProperty(pinia, usageId, {
      mdColSize: 12,
      save: () => {},
      key,
    });
  }

  function buildExpansionPanelTitle(usageId: string, index: number) {
    const titleFields = [buildUsageTitle(usageId, index)];

    if (deletable.value) {
      titleFields.push(buildDeleteUsageButton(usageId));
    }
    return FormExpansionPanelTitle(`${usageId}-title`, titleFields, {
      color: "primary",
      class: "text-white",
    });
  }

  function buildExpansionPanelBody(usageId: string, index: number) {
    const usageStructure = {
      fields: [
        buildCategoryField(usageId, index),
        buildFrequencyField(usageId, index),
      ],
      key: `${usageId}-smoker-fields`,
    };

    if (smoker.value.model === SMOKING_STATUS.PREVIOUSLY) {
      usageStructure.fields.push(buildLastUseDateField(usageId, index));
    }
    return FormExpansionPanelBody(`${usageId}-body`, [usageStructure]);
  }

  function buildExpansionPanel(
    usageId: string,
    index: number,
    panelFields: FormField[]
  ) {
    return FormExpansionPanel(`${usageId}-panel`, panelFields, {
      index,
      "data-testid": `${module}-usage-${index}`,
    });
  }

  function buildExpansionPanels() {
    let panelClasses = "";
    if (deletable.value) {
      panelClasses = "pr-9";
    }

    return FormExpansionPanels(
      "smoking-statuses-container",
      smoking_statuses.value.map((usageId: string, index: number) => {
        const panelFields = [
          buildExpansionPanelTitle(usageId, index),
          buildExpansionPanelBody(usageId, index),
        ];
        return buildExpansionPanel(usageId, index, panelFields);
      }),
      {
        accordion: true,
        class: panelClasses,
        modelValue: activeUsageIndex,
        "onUpdate:modelValue"(v) {
          activeUsageIndex.value = v;
        },
      }
    );
  }

  function buildRoot(): FormBuilderRow {
    return {
      fields: [buildSmokingStatusField()],
      key: `${module}-smoker-root`,
    };
  }

  function buildAddUsage(): FormBuilderRow {
    return {
      fields: [buildAddUsageButton()],
      key: "add-usage",
      componentProps: { class: "text-left" },
    };
  }

  function buildUsages(): FormBuilderRow {
    return { fields: [buildExpansionPanels()], key: "usages-list" };
  }

  function buildStructure() {
    const forms: FormBuilderRow[] = [buildRoot()];
    if (!showSmokingUsage.value) return forms;

    forms.push(buildUsages(), buildAddUsage());

    return forms;
  }

  function validate(args: Partial<{ throwOnFailure: boolean }> = {}) {
    const throwOnFailure = Boolean(args.throwOnFailure);
    try {
      validateFormStructure(buildStructure(), true);
    } catch (e) {
      if (throwOnFailure) throw e;
      if (Array.isArray(e)) {
        const parentPanel = e[0].parents.find(
          (v: FormField) => v && "is" in v && v.is === "VExpansionPanel"
        );
        if (parentPanel)
          activeUsageIndex.value = parentPanel.componentProps.index;
      }
      return false;
    }
    if (smoker.value.model === SMOKING_STATUS.NEVER) return true;

    const validSmokingStatusCount = smoking_statuses.value.length > 0;
    if (throwOnFailure && !validSmokingStatusCount) {
      throw [
        { key: "smoking_statuses", property: { errorMessages: ["required"] } },
      ];
    }

    return validSmokingStatusCount;
  }

  // NOTE(tim): this is a side effect, should be called by the implementation instead of assumed here.
  if (
    !isServerSide() &&
    showSmokingUsage.value &&
    !smoking_statuses.value.length
  ) {
    addUsage();
  }

  const reactiveDependencies = [
    smokingStatusCount,
    showSmokingUsage,
    smokingType,
  ];

  return { buildStructure, validate, activeUsageIndex, reactiveDependencies };
}
